/**
 * Risk Assessment Quiz Mock Data Panel
 *
 * 开发环境下的风险测评Mock工具面板
 * 提供可视化的界面用于快速填充问卷
 *
 * @module mockDataGenerator.demo
 */

import { IRARiskType } from "../types/enum";
import { Button } from "antd";
import { FormInstance } from "antd/lib";
import { useState } from "react";
import {
  generateRandomRiskType,
  generateRiskAssessmentMockData,
  QuizDataContent,
  RISK_ASSESSMENT_PRESETS,
} from "./mockDataGenerator";

interface RiskAssessmentMockDataPanelProps {
  form: FormInstance;
  quizData?: QuizDataContent;
}

/**
 * 风险测评Mock数据生成工具面板
 */
export function RiskAssessmentMockDataPanel({
  form,
  quizData,
}: RiskAssessmentMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [currentRiskType, setCurrentRiskType] = useState<IRARiskType | null>(
    null
  );

  if (!quizData) {
    return null;
  }

  /**
   * 填充表单数据
   */
  const fillFormData = (riskType: IRARiskType) => {
    const mockData = generateRiskAssessmentMockData(quizData, {
      targetRiskType: riskType,
    });

    form.setFieldsValue(mockData);

    const agreement = form.getFieldValue("agreement");
    if (!agreement) {
      console.log("🔧 模拟点击协议确认");
      (
        document.querySelector("label.ant-checkbox-wrapper") as HTMLLabelElement
      )?.click();
    } else {
      console.log("🔧 协议确认已勾选");
    }

    setCurrentRiskType(riskType);

    console.log("✅ 已填充风险测评数据");
    console.log("📊 目标类型:", riskType);
    console.log("📝 表单数据:", mockData);
  };

  /**
   * 预设场景：保守型
   */
  const handleFillConservative = () => {
    fillFormData(RISK_ASSESSMENT_PRESETS.CONSERVATIVE.riskType);
  };

  /**
   * 预设场景：谨慎型
   */
  const handleFillCautious = () => {
    fillFormData(RISK_ASSESSMENT_PRESETS.CAUTIOUS.riskType);
  };

  /**
   * 预设场景：稳健型
   */
  const handleFillModerate = () => {
    fillFormData(RISK_ASSESSMENT_PRESETS.MODERATE.riskType);
  };

  /**
   * 预设场景：积极型
   */
  const handleFillAggressive = () => {
    fillFormData(RISK_ASSESSMENT_PRESETS.AGGRESSIVE.riskType);
  };

  /**
   * 预设场景：激进型
   */
  const handleFillVeryAggressive = () => {
    fillFormData(RISK_ASSESSMENT_PRESETS.VERY_AGGRESSIVE.riskType);
  };

  /**
   * 随机生成
   */
  const handleFillRandom = () => {
    const randomRiskType = generateRandomRiskType();
    fillFormData(randomRiskType);
  };

  /**
   * 清空表单
   */
  const handleClear = () => {
    form.resetFields();
    setCurrentRiskType(null);
    console.log("🗑️ 已清空表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  // 风险类型名称映射
  const riskTypeNames: Record<IRARiskType, string> = {
    [IRARiskType.IRA_RISK_CONSERVATIVE]: "保守型",
    [IRARiskType.IRA_RISK_CAUTIOUS]: "谨慎型",
    [IRARiskType.IRA_RISK_MODERATE]: "稳健型",
    [IRARiskType.IRA_RISK_AGGRESSIVE]: "积极型",
    [IRARiskType.IRA_RISK_VERY_AGGRESSIVE]: "激进型",
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            📊
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "360px",
            maxHeight: "700px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              📊 风险测评 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "600px" }}
          >
            {/* 当前类型显示 */}
            {currentRiskType && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="text-sm font-semibold text-blue-800">
                  当前类型
                </div>
                <div className="text-2xl font-bold text-blue-600 mt-1">
                  {riskTypeNames[currentRiskType]}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  {RISK_ASSESSMENT_PRESETS[
                    Object.keys(RISK_ASSESSMENT_PRESETS).find(
                      (key) =>
                        RISK_ASSESSMENT_PRESETS[
                          key as keyof typeof RISK_ASSESSMENT_PRESETS
                        ].riskType === currentRiskType
                    ) as keyof typeof RISK_ASSESSMENT_PRESETS
                  ]?.description || ""}
                </div>
              </div>
            )}

            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🎯 预设场景
              </div>

              <Button size="small" block onClick={handleFillConservative}>
                {RISK_ASSESSMENT_PRESETS.CONSERVATIVE.name}
              </Button>

              <Button size="small" block onClick={handleFillCautious}>
                {RISK_ASSESSMENT_PRESETS.CAUTIOUS.name}
              </Button>

              <Button size="small" block onClick={handleFillModerate}>
                {RISK_ASSESSMENT_PRESETS.MODERATE.name}
              </Button>

              <Button size="small" block onClick={handleFillAggressive}>
                {RISK_ASSESSMENT_PRESETS.AGGRESSIVE.name}
              </Button>

              <Button size="small" block onClick={handleFillVeryAggressive}>
                {RISK_ASSESSMENT_PRESETS.VERY_AGGRESSIVE.name}
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🔧 其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
              <Button size="small" block danger onClick={handleClear}>
                清空表单
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 使用说明 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                ℹ️ 使用说明
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• 选择预设场景快速填充问卷</div>
                <div>• 根据风险类型自动选择答案</div>
                <div>• 共{quizData.questions.length}道题</div>
                <div>• 风险类型从保守到激进</div>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="font-semibold mb-1">5种风险类型：</div>
                  <div>保守型 - 风险承受能力最低</div>
                  <div>谨慎型 - 风险承受能力较低</div>
                  <div>稳健型 - 风险承受能力中等</div>
                  <div>积极型 - 风险承受能力较高</div>
                  <div>激进型 - 风险承受能力最高</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RiskAssessmentMockDataPanel;
